import { useEffect, useRef } from 'react';
import { reduced } from '../lib/motion';
import './Backdrop.css';

/* ──────────────────────────────────────────────────────────────
   WebGL flow-field backdrop.
   Domain-warped fBm rendered as contour bands — a latent-space
   look that reacts to the pointer. Falls back to a canvas-2D
   node graph when WebGL is unavailable.
   ────────────────────────────────────────────────────────────── */

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;

uniform vec2  uRes;
uniform float uTime;
uniform vec2  uMouse;
uniform float uScroll;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = rot * p * 2.02;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
  float t = uTime * 0.035;

  vec2 m = (uMouse - 0.5 * uRes) / uRes.y;
  float md = length(uv - m);
  float pull = exp(-md * 2.4);

  /* domain warp */
  vec2 q = vec2(fbm(uv * 1.6 + t), fbm(uv * 1.6 + vec2(5.2, 1.3) - t));
  vec2 r = vec2(
    fbm(uv * 1.9 + 3.4 * q + vec2(1.7, 9.2) + t * 1.4),
    fbm(uv * 1.9 + 3.4 * q + vec2(8.3, 2.8) - t * 1.1)
  );
  float f = fbm(uv * 1.7 + 3.2 * r + pull * 0.55 + uScroll * 0.35);

  /* contour bands */
  float bands = f * 7.0;
  float line = abs(fract(bands) - 0.5);
  float w = fwidth(bands) * 1.6;
  float contour = 1.0 - smoothstep(0.0, w, line - 0.015);

  /* field brightness */
  float depth = smoothstep(0.2, 0.95, f);

  vec3 base = vec3(0.027, 0.030, 0.040);
  vec3 cool = vec3(0.10, 0.12, 0.155);
  vec3 volt = vec3(0.851, 1.0, 0.239);

  /* Near-monochrome topography; voltage only follows the pointer. */
  vec3 col = mix(base, cool, depth * 0.30);
  col += contour * cool * 0.34;
  col += contour * volt * pull * 0.30;
  col += volt * pull * pull * 0.028;

  /* vignette */
  float vig = smoothstep(1.30, 0.30, length(uv * vec2(0.85, 1.0)));
  col *= vig;

  /* dither to kill banding */
  col += (hash(gl_FragCoord.xy + uTime) - 0.5) * 0.010;

  gl_FragColor = vec4(col, 1.0);
}
`;

function initGL(canvas) {
  const gl =
    canvas.getContext('webgl', { antialias: false, alpha: false, powerPreference: 'low-power' }) ||
    canvas.getContext('experimental-webgl');
  if (!gl) return null;

  const ext = gl.getExtension('OES_standard_derivatives');
  const header = ext ? '#extension GL_OES_standard_derivatives : enable\n' : '';
  const fragSrc = ext
    ? header + FRAG
    : FRAG.replace('float w = fwidth(bands) * 1.6;', 'float w = 0.05;');

  const compile = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      gl.deleteShader(s);
      return null;
    }
    return s;
  };

  const vs = compile(gl.VERTEX_SHADER, VERT);
  const fs = compile(gl.FRAGMENT_SHADER, fragSrc);
  if (!vs || !fs) return null;

  const prog = gl.createProgram();
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;

  gl.useProgram(prog);

  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  return {
    gl,
    u: {
      res: gl.getUniformLocation(prog, 'uRes'),
      time: gl.getUniformLocation(prog, 'uTime'),
      mouse: gl.getUniformLocation(prog, 'uMouse'),
      scroll: gl.getUniformLocation(prog, 'uScroll'),
    },
  };
}

/* ── Canvas-2D fallback: node graph ───────────────────────────── */
function run2D(canvas, state) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  let nodes = [];
  const LINK = 168;

  const build = (w, h) => {
    const count = Math.max(24, Math.min(70, Math.round((w * h) / 13000)));
    nodes = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.15,
      vy: (Math.random() - 0.5) * 0.15,
      hub: Math.random() < 0.15,
    }));
  };

  let w = 0;
  let h = 0;
  const resize = () => {
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    build(w, h);
  };
  resize();
  window.addEventListener('resize', resize);

  let raf;
  const frame = () => {
    ctx.clearRect(0, 0, w, h);
    for (const n of nodes) {
      n.x += n.vx; n.y += n.vy;
      if (n.x < -30) n.x = w + 30; if (n.x > w + 30) n.x = -30;
      if (n.y < -30) n.y = h + 30; if (n.y > h + 30) n.y = -30;
    }
    ctx.lineWidth = 1;
    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const a = nodes[i]; const b = nodes[j];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d > LINK) continue;
        const fade = 1 - d / LINK;
        const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2;
        const pd = Math.hypot(mx - state.mx, my - state.my);
        const near = pd < 230 ? 1 - pd / 230 : 0;
        ctx.strokeStyle = near > 0.05
          ? `rgba(217,255,61,${(0.05 + near * 0.22) * fade})`
          : `rgba(190,205,225,${0.05 * fade})`;
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      }
    }
    for (const n of nodes) {
      ctx.fillStyle = n.hub ? 'rgba(217,255,61,0.7)' : 'rgba(226,234,246,0.32)';
      ctx.beginPath(); ctx.arc(n.x, n.y, n.hub ? 1.9 : 0.9, 0, Math.PI * 2); ctx.fill();
    }
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
  };
}

export default function Backdrop() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;

    const state = { mx: window.innerWidth / 2, my: window.innerHeight / 2, tx: 0, ty: 0, scroll: 0 };
    const onMove = (e) => { state.tx = e.clientX; state.ty = e.clientY; };
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      state.scroll = max > 0 ? window.scrollY / max : 0;
    };
    state.tx = state.mx; state.ty = state.my;
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    const ctx = initGL(canvas);

    if (!ctx) {
      const stop = run2D(canvas, state);
      const id = setInterval(() => { state.mx = state.tx; state.my = state.ty; }, 16);
      return () => {
        stop();
        clearInterval(id);
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('scroll', onScroll);
      };
    }

    const { gl, u } = ctx;
    const soft = reduced();
    let raf;
    let running = true;
    const t0 = performance.now();

    const resize = () => {
      const scale = window.innerWidth < 900 ? 0.55 : 0.72;
      const w = Math.floor(window.innerWidth * scale);
      const h = Math.floor(window.innerHeight * scale);
      canvas.width = w;
      canvas.height = h;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(u.res, w, h);
    };
    resize();
    window.addEventListener('resize', resize);

    const frame = (now) => {
      state.mx += (state.tx - state.mx) * 0.05;
      state.my += (state.ty - state.my) * 0.05;
      const scale = canvas.width / window.innerWidth;
      gl.uniform1f(u.time, soft ? 12.0 : (now - t0) / 1000);
      gl.uniform2f(u.mouse, state.mx * scale, canvas.height - state.my * scale);
      gl.uniform1f(u.scroll, state.scroll);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (running) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onVis = () => {
      if (document.hidden) { running = false; cancelAnimationFrame(raf); }
      else if (!running) { running = true; raf = requestAnimationFrame(frame); }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return <canvas ref={ref} className="backdrop" aria-hidden="true" />;
}
